import {useEffect, useRef} from 'react';
import {usePrevious} from '../../../../../../common/shared/hooks/usePrevious';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {TalkGroupKey} from '../type/KeyType.type';
import {ZIndex} from '../type/ZIndex.enum';
import {getDataUrlOrUrl} from '../util/getDataUrlOrUrl';

interface TalkViewerBackgroundContainerProps {
  talkGroupId: TalkGroupKey;
}

export function TalkViewerBackgroundContainer({
  talkGroupId,
}: TalkViewerBackgroundContainerProps) {
  const talkGroup = useTypedSelector(
    state => state.talkViewer.talkGroupMap[talkGroupId]
  );
  const preloadedMedia = useTypedSelector(
    state => state.talkViewer.preloadedMedia
  );
  // 배경전환 애니메이션을 위해 기존 배경정보를 가지고 있어야 합니다.
  const prevTalkGroup = usePrevious(talkGroup);

  const animationTimer = useRef(0);
  const coverRef = useRef<HTMLDivElement>(null);
  const animateBgInfo = useRef({
    isProcessing: false,
    background: talkGroup?.background,
    opacity: 1,
    status: false,
  });

  const {id: prevTalkGroupId, background: prevBackground} = prevTalkGroup || {};

  useEffect(() => {
    if (
      !talkGroupId ||
      talkGroupId === prevTalkGroupId ||
      !prevTalkGroupId ||
      !coverRef.current
    ) {
      return;
    }
    if (animateBgInfo.current.isProcessing) {
      window.clearTimeout(animationTimer.current);
    }
    // 애니메이션 시간이 지난 후에 현재 진행중인 애니메이션이 없다고 판단하도록 한다.
    animationTimer.current = window.setTimeout(
      () => {
        animateBgInfo.current.isProcessing = false;
      },
      TRANSITION_TIME * animateBgInfo.current.opacity * 1000
    );
  });

  if (!talkGroup) {
    return null;
  }
  if (talkGroupId !== prevTalkGroupId && prevTalkGroupId && prevBackground) {
    // 애니메이션이 진행중인 경우 isProcessing이 true이다. 이 경우 현재 cover의 opacity값을 받아 그 시점부터 애니메이션을 시작한다.
    if (animateBgInfo.current.isProcessing && coverRef.current) {
      const currentOpacity =
        1 - Number(getComputedStyle(coverRef.current).opacity);
      animateBgInfo.current = {
        isProcessing: true,
        background: prevBackground,
        opacity: currentOpacity,
        status: true,
      };
    } else {
      animateBgInfo.current = {
        isProcessing: true,
        background: prevBackground,
        opacity: 1,
        status: true,
      };
    }
  }

  const background = getDataUrlOrUrl(talkGroup.background, preloadedMedia);
  const animateBackgroud = animateBgInfo.current.background
    ? getDataUrlOrUrl(animateBgInfo.current.background, preloadedMedia)
    : undefined;
  const animationName = `dissolve_${talkGroupId}`;
  return (
    <>
      {/* 실제 배경 */}
      <div
        className="fixed top-0 left-0 h-screen w-full transform-gpu"
        style={{
          background: `center center / cover ${
            background.includes('/') ? `url(${background})` : background
          }`,
          zIndex: ZIndex.Background,
        }}
      />
      {/* 기존 배경 (애니메이션 용도) */}
      <div
        ref={coverRef}
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={twMerge(
          animateBgInfo.current.status && 'animation-background',
          'pointer-events-none fixed top-0 left-0 h-screen w-full transform-gpu'
        )}
        style={{
          background: `center center / cover ${
            animateBackgroud?.includes('/')
              ? `url(${animateBackgroud})`
              : animateBackgroud
          }`,
          zIndex: ZIndex.Background + 1,
        }}
      />
      <style jsx>
        {`
          .animation-background {
            opacity: ${animateBgInfo.current.opacity};
            animation: ${TRANSITION_TIME * animateBgInfo.current.opacity}s ease
              0s 1 normal forwards ${animationName};
          }
        `}
      </style>
      <style jsx global>
        {`
          @keyframes ${animationName} {
            from {
              opacity: ${animateBgInfo.current.opacity};
            }
            to {
              opacity: 0;
            }
          }
        `}
      </style>
    </>
  );
}

const TRANSITION_TIME = 1.5;
